import {
  emailFormCtaClick,
  isMobileViewport,
  sendHeaderData,
  whereToBuyClick,
} from '@nulo/gtm';

/**
 * @extends {HTMLElement}
 */
class NuloHeader extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #openTimer = null;

  /** @type {number | null} */
  #closeTimer = null;

  /** @type {HTMLElement | null} */
  #openMenuItem = null;

  /** @type {HTMLElement | null} */
  #openPanel = null;

  /** @type {number} */
  #scrollPosition = 0;

  /** @type {number | null} */
  #mobileCloseTimer = null;

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const openDelay = parseInt(this.dataset.openDelay || '200', 10);
    const closeDelay = parseInt(this.dataset.closeDelay || '200', 10);

    this.#syncBarHeight();
    window.addEventListener('resize', () => this.#syncBarHeight(), { signal });

    const megaMenus = this.querySelector('.nulo-header__mega-menus');

    this.querySelectorAll('.nulo-header__menu-item--has-children').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      item.addEventListener(
        'mouseenter',
        () => {
          this.#clearTimers();
          this.#openTimer = window.setTimeout(() => this.#openDesktopMenu(item), openDelay);
        },
        { signal }
      );

      item.addEventListener(
        'mouseleave',
        () => {
          this.#clearTimers();
          this.#closeTimer = window.setTimeout(() => this.#closeDesktopMenu(), closeDelay);
        },
        { signal }
      );
    });

    megaMenus?.addEventListener(
      'mouseenter',
      () => {
        this.#clearTimers();
      },
      { signal }
    );

    megaMenus?.addEventListener(
      'mouseleave',
      () => {
        this.#clearTimers();
        this.#closeTimer = window.setTimeout(() => this.#closeDesktopMenu(), closeDelay);
      },
      { signal }
    );

    this.querySelectorAll('.nulo-header__menu-toggle').forEach((button) => {
      button.addEventListener('click', (event) => this.#toggleDesktopMenu(event), { signal });
      button.addEventListener('keydown', (event) => this.#onDesktopToggleKeydown(event), { signal });
    });

    const mobileOpen = this.querySelector('[data-mobile-open]');
    if (mobileOpen instanceof HTMLButtonElement) {
      mobileOpen.dataset.openLabel = mobileOpen.getAttribute('aria-label') || '';
      mobileOpen.addEventListener('click', () => this.#toggleMobileMenu(mobileOpen), { signal });
    }

    const mobileDismiss = this.querySelector('[data-mobile-dismiss]');
    if (mobileDismiss instanceof HTMLButtonElement && mobileOpen instanceof HTMLButtonElement) {
      mobileDismiss.addEventListener('click', () => this.#closeMobileMenu(mobileOpen), { signal });
    }

    this.querySelectorAll('.nulo-header__mobile-accordion').forEach((button) => {
      button.addEventListener('click', (event) => this.#toggleMobileAccordion(event), { signal });
    });

    this.querySelectorAll('[data-search-open]').forEach((button) => {
      button.addEventListener('click', () => this.#onSearchToggle(button), { signal });
    });

    this.addEventListener('click', (event) => this.#onHeaderClick(event), { signal });
    document.addEventListener('keydown', (event) => this.#onDocumentKeydown(event), { signal });
    document.addEventListener('nulo-search:opened', () => {
      this.#closeMobileMenuFromSearch();
      this.#setHappyFoxBehindOverlay(true);
    }, { signal });
    document.addEventListener('nulo-search:closed', () => this.#setHappyFoxBehindOverlay(false), { signal });
  }

  disconnectedCallback() {
    this.#clearTimers();
    this.#clearMobileCloseTimer();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#unlockBodyScroll();
  }

  #syncBarHeight() {
    const bar = this.querySelector('.nulo-header__bar');
    if (!(bar instanceof HTMLElement)) return;

    const height = Math.round(bar.getBoundingClientRect().height);
    this.style.setProperty('--nulo-header-bar-height', `${height - 1}px`);
  }

  #clearTimers() {
    if (this.#openTimer != null) {
      window.clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }
    if (this.#closeTimer != null) {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #clearMobileCloseTimer() {
    if (this.#mobileCloseTimer != null) {
      window.clearTimeout(this.#mobileCloseTimer);
      this.#mobileCloseTimer = null;
    }
  }

  #mobilePanelCloseDuration() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    return 300;
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #panelForItem(item) {
    const handle = item.dataset.menuHandle;
    if (!handle) return null;

    const panel = this.querySelector(`.mega-menu[data-menu-handle="${handle}"]`);
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @param {HTMLElement} item
   */
  #openDesktopMenu(item) {
    const panel = this.#panelForItem(item);
    if (!panel) return;

    if (this.#openMenuItem && this.#openMenuItem !== item) {
      this.#closeDesktopMenu();
    }

    this.#openMenuItem = item;
    this.#openPanel = panel;

    item.classList.add('nulo-header__menu-item--open');

    const toggle = item.querySelector('.nulo-header__menu-toggle');
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    this.classList.add('nulo-header--mega-open');

    if (toggle instanceof HTMLButtonElement) toggle.setAttribute('aria-expanded', 'true');
  }

  #closeDesktopMenu() {
    if (this.#openMenuItem) {
      this.#openMenuItem.classList.remove('nulo-header__menu-item--open');

      const toggle = this.#openMenuItem.querySelector('.nulo-header__menu-toggle');
      if (toggle instanceof HTMLButtonElement) toggle.setAttribute('aria-expanded', 'false');
    }

    if (this.#openPanel) {
      this.#openPanel.classList.remove('is-open');
      this.#openPanel.setAttribute('aria-hidden', 'true');
    }

    this.#openMenuItem = null;
    this.#openPanel = null;
    this.classList.remove('nulo-header--mega-open');
  }

  /**
   * @param {Event} event
   */
  #toggleDesktopMenu(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const item = button.closest('.nulo-header__menu-item--has-children');
    if (!(item instanceof HTMLElement)) return;

    const isOpen = item === this.#openMenuItem;
    if (isOpen) {
      this.#closeDesktopMenu();
      return;
    }

    this.#openDesktopMenu(item);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onDesktopToggleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#toggleDesktopMenu(event);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown(event) {
    if (event.key !== 'Escape') return;

    const search = this.querySelector('nulo-header-search');
    if (search?.classList.contains('is-active') && typeof search.close === 'function') {
      search.close();
      return;
    }

    if (this.#openMenuItem) {
      const toggle = this.#openMenuItem.querySelector('.nulo-header__menu-toggle');
      this.#closeDesktopMenu();
      if (toggle instanceof HTMLButtonElement) toggle.focus();
    }

    const overlay = this.querySelector('[data-mobile-overlay]');
    const mobileToggle = this.querySelector('[data-mobile-open]');
    if (
      overlay instanceof HTMLElement &&
      overlay.classList.contains('is-active') &&
      mobileToggle instanceof HTMLButtonElement
    ) {
      this.#closeMobileMenu(mobileToggle);
    }
  }

  /**
   * @param {HTMLButtonElement} toggle
   */
  #toggleMobileMenu(toggle) {
    const overlay = this.querySelector('[data-mobile-overlay]');
    if (!(overlay instanceof HTMLElement)) return;

    if (overlay.classList.contains('is-closing')) return;

    if (!overlay.classList.contains('is-active')) {
      this.#openMobileMenu(toggle, overlay);
      return;
    }

    this.#closeMobileMenu(toggle, overlay);
  }

  /**
   * @param {HTMLButtonElement} toggle
   * @param {HTMLElement} overlay
   */
  #openMobileMenu(toggle, overlay) {
    this.#clearMobileCloseTimer();
    this.#closeSearch();
    this.#closeDesktopMenu();
    this.#setHappyFoxBehindOverlay(true);
    this.#syncBarHeight();

    overlay.classList.remove('is-closing');
    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');
    toggle.classList.add('active');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', toggle.dataset.closeLabel || 'Close menu');
    this.#lockBodyScroll();
  }

  /**
   * @param {HTMLButtonElement} toggle
   * @param {HTMLElement} [overlay]
   */
  #closeMobileMenu(toggle, overlay) {
    const panel = overlay || this.querySelector('[data-mobile-overlay]');
    if (!(panel instanceof HTMLElement)) return;
    if (!panel.classList.contains('is-active') || panel.classList.contains('is-closing')) return;

    toggle.classList.remove('active');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', toggle.dataset.openLabel || 'Open menu');

    panel.classList.add('is-closing');
    panel.classList.remove('is-active');
    panel.setAttribute('aria-hidden', 'true');

    this.#clearMobileCloseTimer();
    const duration = this.#mobilePanelCloseDuration();
    if (duration === 0) {
      this.#finishMobileMenuClose(panel);
      return;
    }

    this.#mobileCloseTimer = window.setTimeout(() => {
      this.#finishMobileMenuClose(panel);
    }, duration);
  }

  /**
   * @param {HTMLElement} overlay
   */
  #finishMobileMenuClose(overlay) {
    this.#clearMobileCloseTimer();
    overlay.classList.remove('is-closing');
    this.#setHappyFoxBehindOverlay(false);
    this.#unlockBodyScroll();
  }

  #lockBodyScroll() {
    this.#scrollPosition = window.pageYOffset;
    document.body.classList.add('body-scroll-lock');
    document.body.style.setProperty('top', `-${this.#scrollPosition}px`, 'important');
  }

  #unlockBodyScroll() {
    if (!document.body.classList.contains('body-scroll-lock')) return;

    document.body.classList.remove('body-scroll-lock');
    document.body.style.removeProperty('top');
    window.scrollTo({ top: this.#scrollPosition, behavior: 'instant' });
  }

  /**
   * @param {Event} event
   */
  #toggleMobileAccordion(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const item = button.closest('.nulo-header__mobile-item');
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;

    if (!(item instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    const isOpen = !panel.hidden;
    panel.hidden = isOpen;
    button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    item.classList.toggle('nulo-header__mobile-item--open', !isOpen);

    const row = item.querySelector('.nulo-header__mobile-row');
    if (row instanceof HTMLElement) {
      row.classList.toggle('mob-megamenu-sec', isOpen);
    }

    panel.classList.toggle('mob-megamenu-sec', !isOpen);
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #onSearchToggle(button) {
    const search = this.querySelector('nulo-header-search');
    if (search && typeof search.toggle === 'function') {
      if (!search.classList.contains('is-active')) {
        const mobileToggle = this.querySelector('[data-mobile-open]');
        if (mobileToggle instanceof HTMLButtonElement) {
          this.#closeMobileMenu(mobileToggle);
        }
      }
      search.toggle();
      return;
    }

    document.dispatchEvent(new CustomEvent('nulo-search:open'));
  }

  #closeMobileMenuFromSearch() {
    const mobileToggle = this.querySelector('[data-mobile-open]');
    if (mobileToggle instanceof HTMLButtonElement) {
      this.#closeMobileMenu(mobileToggle);
    }
  }

  #closeSearch() {
    const search = this.querySelector('nulo-header-search');
    if (search && typeof search.close === 'function') {
      search.close();
    }
  }

  /**
   * @param {boolean} behindOverlay
   */
  #setHappyFoxBehindOverlay(behindOverlay) {
    window.NuloHappyFox?.setBehindOverlay?.(behindOverlay);
  }

  /**
   * @param {Event} event
   */
  #onHeaderClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const menuLink = target.closest('[data-gtm-menu-main]');
    if (menuLink instanceof HTMLElement) {
      const main = menuLink.dataset.gtmMenuMain || '';
      const sub = menuLink.dataset.gtmMenuSub || '';
      sendHeaderData(main, sub || undefined);
    }

    if (target.closest('[data-gtm-email-cta]')) {
      emailFormCtaClick(isMobileViewport(), 'header');
    }

    if (target.closest('[data-gtm-where-to-buy]')) {
      whereToBuyClick();
    }

    const closeLink = target.closest('[data-gtm-close-mobile]');
    const mobileToggle = this.querySelector('[data-mobile-open]');
    if (closeLink && mobileToggle instanceof HTMLButtonElement) {
      this.#closeMobileMenu(mobileToggle);
    }
  }
}

if (!customElements.get('nulo-header')) {
  customElements.define('nulo-header', NuloHeader);
}
